import React, { useEffect } from "react";
import { observer } from "mobx-react";
import { useLocation } from "react-router-dom";
import copilotDrawerStore from "Stores/copilotDrawerStore";
import agentsPanelStore from "Stores/agentsPanelStore";
import updatesPanelStore, { REFRESH_EVERY_MS } from "Stores/updatesPanelStore";
import UpdatesPanel, { MegaphoneGlyph } from "Global/RightRail/UpdatesPanel";
import { currentSurface } from "Pages/Dashboard/Copilot/agent/copilotContext";
import ShellTooltip from "Components/common/ShellTooltip";
import watchOverlays from "Utils/overlayDim";
import Utils from "Utils/utils";
import { MXP_EVENT, PAGE_TYPE } from "Static/MixPanelEvents";
import { REQUEST_DEMO_CALENDAR_LINK } from "Static/constant";
import "./headerAssist.scss";

/*
 * Copilot and What's new, moved from the right rail into the top bar (design row G).
 * Copilot opens docked on the LEFT, beside the nav; ⌘J toggles it from anywhere.
 */

const toggleCopilot = () => {
  const { surface, entityId } = currentSurface();
  agentsPanelStore.close();
  updatesPanelStore.close();
  copilotDrawerStore.toggle(surface, entityId);
};

const IS_MAC = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent || "");

export const CopilotHeaderButton = observer(() => {
  // The full-page copilot is its own surface; the drawer stands down there.
  const onCopilotPage = useLocation().pathname.startsWith("/copilot");

  useEffect(() => {
    const onKey = e => {
      // Not while typing: in a field or the email editor the keys belong to the text.
      const t = e.target;
      const typing = t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName || ""));
      if (!typing && (e.metaKey || e.ctrlKey) && (e.key || "").toLowerCase() === "j") {
        e.preventDefault();
        if (!window.location.pathname.startsWith("/copilot")) toggleCopilot();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (onCopilotPage) return null;
  const on = copilotDrawerStore.open;
  return (
    <button
      type="button"
      className={`sgHeaderCopilot${on ? " isOn" : ""}`}
      aria-pressed={on}
      onClick={toggleCopilot}
    >
      <span className="sgHeaderCopilot__spark" aria-hidden="true">
        ✦
      </span>
      Copilot
      <span className="sgHeaderCopilot__key">{IS_MAC ? "⌘J" : "Ctrl+J"}</span>
    </button>
  );
});

export const UpdatesHeaderButton = observer(() => {
  // Same polling the rail did: once the page settles, then on an interval and on focus.
  useEffect(() => {
    const stopWatchingOverlays = watchOverlays();
    const load = setTimeout(updatesPanelStore.load, 4000);
    const refresh = setInterval(updatesPanelStore.refresh, REFRESH_EVERY_MS);
    window.addEventListener("focus", updatesPanelStore.refresh);
    return () => {
      stopWatchingOverlays();
      clearTimeout(load);
      clearInterval(refresh);
      window.removeEventListener("focus", updatesPanelStore.refresh);
    };
  }, []);

  const unseen = updatesPanelStore.unseenCount;
  const toggle = () => {
    if (!updatesPanelStore.open) copilotDrawerStore.close();
    agentsPanelStore.close();
    updatesPanelStore.toggle();
  };
  return (
    <>
      <ShellTooltip label="What's new">
        <button
          type="button"
          className={`sgHeaderUpdates${updatesPanelStore.open ? " isOn" : ""}`}
          aria-pressed={updatesPanelStore.open}
          aria-label={unseen ? `What's new, ${unseen} unread` : "What's new"}
          onClick={toggle}
        >
          <MegaphoneGlyph />
          {unseen > 0 && (
            <span className="sgHeaderUpdates__count" aria-hidden="true">
              {unseen > 9 ? "9+" : unseen}
            </span>
          )}
        </button>
      </ShellTooltip>
      <UpdatesPanel />
    </>
  );
});

// Book a demo, moved from the right rail; shown to every user.
export const BookDemoHeaderButton = () => (
  <button
    type="button"
    className="sgHeaderDemo"
    onClick={() => {
      Utils.mixpanelTrack(MXP_EVENT.REQUEST_DEMO_HEADER, { pageType: PAGE_TYPE.header });
      Utils.windowRedirect(REQUEST_DEMO_CALENDAR_LINK, "_blank", true);
    }}
  >
    <i className="material-icons-outlined">calendar_today</i>
    Book a demo
  </button>
);
